import clsx from "clsx";
import { ReactNode } from "react";

export interface Metric {
  icon: ReactNode;
  value: string;
  unit: string;
  /** Omitted for metrics that only carry a value and a unit, e.g. "5G / Unlimitiert". */
  label?: string;
}

export interface MetricTilesProps {
  metrics: Metric[];
  /**
   * 2 for the detail view's "Dein Abo" container, 4 for the wider
   * product-card footer.
   */
  gap?: 2 | 4;
  className?: string;
}

/**
 * Row of equally wide quaternary tiles stating a headline figure with its unit
 * (and optionally what it measures), e.g. the download/upload speeds of a
 * Flat Home or data subscription.
 */
export const MetricTiles = ({
  metrics,
  gap = 2,
  className,
}: MetricTilesProps) => (
  <div
    // items-stretch (the default) keeps every tile as tall as the busiest one,
    // which is how the design draws them.
    className={clsx("flex w-full", gap === 2 ? "gap-2" : "gap-4", className)}
  >
    {metrics.map((metric) => (
      <div
        key={`${metric.label ?? ""}-${metric.value}-${metric.unit}`}
        // No fixed height: the tallest tile comes out at the design's height
        // on its own, and the shorter ones stretch to match it. The "Dein
        // Abo" tiles (gap=2) use taller 28px vertical padding than the
        // product-card footer's (gap=4) so their 24px icons still line up
        // on one row across all three tiles at the design's 132px height.
        className={clsx(
          "flex flex-1 flex-col items-center justify-center gap-2 overflow-hidden rounded-md bg-surface-quaternary px-4",
          gap === 2 ? "py-7" : "py-4",
        )}
      >
        <span className="shrink-0 text-foreground-primary">{metric.icon}</span>
        <div className="flex flex-col items-center gap-1 whitespace-nowrap text-center">
          <p className="text-body-base font-semibold leading-none text-foreground-primary">
            {metric.value}
          </p>
          <p className="text-body-xs leading-none text-foreground-secondary">
            {metric.unit}
          </p>
          {metric.label && (
            <p className="text-body-xs font-semibold leading-md text-foreground-primary">
              {metric.label}
            </p>
          )}
        </div>
      </div>
    ))}
  </div>
);
